import React, { useContext, useEffect, useState, useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import 'styled-components/macro';
import { AuthContext } from 'context/authContext';
import Button from 'components/atoms/Button';
import Field from 'components/molecules/Field';
import Form, { useForm } from 'components/molecules/Form';
import Toast from 'components/molecules/Toast';
import { convertToFormData } from 'helpers/common';
import { StyledProduct } from './Product.styles';
import productCategoryService from 'services/productCategoryService';
import productIndustryService from 'services/ProductIndustryService';
import productStyleService from 'services/ProductStylesService';
import productService from 'services/ProductService';
import {
  buildProductFormState,
  buildProductPayload,
  galleryImages,
  initialFaqs,
  initialFeaturedImages,
  initialFinishes,
  initialInserts,
  initialMaterials,
} from './productFormUtils';
import ProductBasicDetails from './sections/ProductBasicDetails';
import FeaturedImagesSection from './sections/FeaturedImagesSection';
import DielineSection from './sections/DielineSection';
import CustomizationSection from './sections/CustomizationSection';
import MaterialsSection from './sections/MaterialsSection';
import FinishesSection from './sections/FinishesSection';
import InsertsSection from './sections/InsertsSection';
import DesignSection from './sections/DesignSection';
import PersonalizationSection from './sections/PersonalizationSection';
import FaqsSection from './sections/FaqsSection';

function CreateProduct({ isEdit, productData, onClose = () => {} }) {
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const { refetch } = useContext(AuthContext);

  const [galleryFields, setGalleryFields] = useState(galleryImages);
  const [faqFields, setFaqFields] = useState(initialFaqs);
  const [featuredImageFields, setFeaturedImageFields] = useState(initialFeaturedImages);
  const [materialCards, setMaterialCards] = useState(initialMaterials);
  const [finishCards, setFinishCards] = useState(initialFinishes);
  const [insertCards, setInsertCards] = useState(initialInserts);

  const { product_categories_data } = productCategoryService.GetProductCategories({ getAll: true }, refetch);
  const { product_industries_data } = productIndustryService.GetProductIndustries({ getAll: true }, refetch);
  const { product_styles_data } = productStyleService.GetProductStyles({ getAll: true }, refetch);

  const categoryOpts = useMemo(() => {
    return product_categories_data?.categories.map(category => ({
      label: category?.title,
      value: category?._id,
    }));
  }, [product_categories_data]);

  const industryOpts = useMemo(() => {
    return product_industries_data?.industries?.map(industry => ({
      label: industry?.title,
      value: industry?._id,
    }));
  }, [product_industries_data]);

  const styleOpts = useMemo(() => {
    return product_styles_data?.styles?.map(style => ({
      label: style?.title,
      value: style?._id,
    }));
  }, [product_styles_data]);

  useEffect(() => {
    if (!isEdit || !productData) return;

    const { gallery, featuredImages, materials, finishes, inserts, faqs, values } = buildProductFormState(productData);

    if (gallery) setGalleryFields(gallery);
    if (featuredImages) setFeaturedImageFields(featuredImages);
    if (materials) setMaterialCards(materials);
    if (finishes) setFinishCards(finishes);
    if (inserts) setInsertCards(inserts);
    if (faqs) setFaqFields(faqs);

    form.setFieldsValue(values);
  }, [isEdit, productData]);

  const onSubmit = async data => {
    setLoading(true);
    const payload = buildProductPayload({
      data,
      featuredImageFields,
      galleryFields,
      materialCards,
      finishCards,
      insertCards,
      faqFields,
    });

    if (isEdit && data?.slug && data?.slug !== '') {
      payload.slug = data?.slug;
    }
    try {
      let res;
      if (isEdit) {
        res = await productService.updateProduct(productData?._id, convertToFormData(payload));
      } else {
        res = await productService.createProduct(convertToFormData(payload));
      }
      refetch();
      onClose();
      if (res?.success)
        Toast({
          type: 'success',
          message: res?.message || 'Post saved successfully',
        });
    } catch (ex) {
      Toast({
        type: 'error',
        message: ex.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledProduct>
      <Form form={form} onSubmit={onSubmit}>
        <ProductBasicDetails
          form={form}
          categoryOpts={categoryOpts}
          industryOpts={industryOpts}
          styleOpts={styleOpts}
        />

        <FeaturedImagesSection fields={featuredImageFields} setFields={setFeaturedImageFields} form={form} />

        <DielineSection form={form} />

        <CustomizationSection />

        <MaterialsSection materials={materialCards} setMaterials={setMaterialCards} form={form} />

        <FinishesSection finishes={finishCards} setFinishes={setFinishCards} form={form} />

        <InsertsSection inserts={insertCards} setInserts={setInsertCards} form={form} />

        <DesignSection fields={galleryFields} setFields={setGalleryFields} form={form} />

        <PersonalizationSection form={form} />

        <FaqsSection faqs={faqFields} setFaqs={setFaqFields} />

        {isEdit && (
          <Form.Item
            sm
            type="text"
            label="Slug"
            name="slug"
            placeholder="the-future-of-technology-how-innovations-are-shaping-our-lives">
            <Field />
          </Form.Item>
        )}
        <Button loading={loading} type="primary" htmlType="submit" style={{ margin: '0 auto' }}>
          Save
        </Button>
      </Form>
    </StyledProduct>
  );
}

export default CreateProduct;
